const LOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 5 * 60 * 1000 // 5 minutes
};

export class LocationService {
  private watchId: number | null = null;
  private lastLocation: DeviceLocation | null = null;
  private subscribers: Array<(location: DeviceLocation | null) => void> = [];

  async getCurrentLocation(): Promise<DeviceLocation | null> {
    if (typeof window === 'undefined') return null;
    
    try {
      return await new Promise((resolve) => {
        if (!navigator.geolocation) {
          console.warn('Geolocation is not supported by this browser');
          resolve(this.lastLocation);
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location: DeviceLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp
            };
            this.lastLocation = location;
            this.notifySubscribers(location);
            this.cacheLocation(location);
            resolve(location);
          },
          (error) => {
            console.warn('Error getting location:', error);
            resolve(this.getCachedLocation());
          },
          LOCATION_OPTIONS
        );
      });
    } catch (error) {
      console.error('Error in getCurrentLocation:', error);
      return this.getCachedLocation();
    }
  }

  startWatching() {
    if (this.watchId !== null || typeof window === 'undefined') return;

    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser');
      return;
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const location: DeviceLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };
        this.lastLocation = location;
        this.notifySubscribers(location);
        this.cacheLocation(location);
      },
      (error) => {
        console.warn('Error watching location:', error);
      },
      LOCATION_OPTIONS
    );
  }

  stopWatching() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  subscribe(callback: (location: DeviceLocation | null) => void): () => void {
    this.subscribers.push(callback);
    // Immediately notify with last known location if available
    if (this.lastLocation) {
      callback(this.lastLocation);
    } else {
      this.getCurrentLocation().then(location => {
        if (location) callback(location);
      });
    }
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  getLastKnownLocation(): DeviceLocation | null {
    return this.lastLocation || this.getCachedLocation();
  }

  private notifySubscribers(location: DeviceLocation | null) {
    this.subscribers.forEach(callback => {
      try {
        callback(location);
      } catch (error) {
        console.error('Error in location subscriber:', error);
      }
    });
  }

  private cacheLocation(location: DeviceLocation) {
    try {
      localStorage.setItem('last_known_location', JSON.stringify(location));
    } catch (error) {
      console.error('Error caching location:', error);
    }
  }

  private getCachedLocation(): DeviceLocation | null {
    try {
      const cached = localStorage.getItem('last_known_location');
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Error getting cached location:', error);
      return null;
    }
  }
}

export const locationService = new LocationService();

// Start watching location when the module is loaded
if (typeof window !== 'undefined') {
  locationService.startWatching();
}

export interface DeviceLocation {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp: number;
  altitude?: number | null;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
}
